import { useState, useEffect } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "pwa-install-dismissed";

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsStandalone(true);
      return;
    }
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const triggerInstall = async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === "accepted") {
      localStorage.setItem(DISMISS_KEY, "installed");
      return true;
    }
    return false;
  };

  return { deferredPrompt, isStandalone, triggerInstall };
}

export default function InstallPrompt() {
  const { deferredPrompt, isStandalone, triggerInstall } = useInstallPrompt();
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY)) setHidden(true);
  }, []);

  if (isStandalone || hidden) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "dismissed");
    setHidden(true);
  };

  const handleInstall = async () => {
    const accepted = await triggerInstall();
    if (accepted) setHidden(true);
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-md rounded-xl border border-border bg-card p-4 shadow-lg flex items-center gap-3 animate-in slide-in-from-bottom-4">
      <Download className="h-6 w-6 shrink-0 text-primary" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">Install Dyna Agency</p>
        <p className="text-xs text-muted-foreground truncate">
          {deferredPrompt ? "Add to your home screen for quick access" : "Tap Share → Add to Home Screen"}
        </p>
      </div>
      {deferredPrompt && (
        <Button size="sm" onClick={handleInstall}>Install</Button>
      )}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss install prompt"
        className="text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
